<template>
  <div
    class="fob-op-search"
    :class="{
      'is-open': isOpen,
      'is-fixed-panel': fixedPanel,
      'is-dark': dark,
    }"
  >
    <div class="input-group input-group-sm">
      <span class="input-group-text bg-white">
        <i class="fas fa-search text-muted" aria-hidden="true"></i>
      </span>
      <input
        ref="input"
        v-model="query"
        type="search"
        class="form-control"
        autocomplete="off"
        :placeholder="resolvedPlaceholder"
        :aria-label="resolvedPlaceholder"
        @input="onInput"
        @focus="onFocus"
        @keydown.down.prevent="moveHighlight(1)"
        @keydown.up.prevent="moveHighlight(-1)"
        @keydown.enter.prevent="chooseHighlighted"
        @keydown.esc.prevent="close"
      />
      <button
        v-if="query"
        type="button"
        class="btn btn-outline-secondary"
        :title="$t('Clear')"
        @click="clear"
      >
        <i class="fas fa-times" aria-hidden="true"></i>
      </button>
      <span v-if="searching" class="input-group-text bg-white">
        <span
          class="spinner-border spinner-border-sm text-primary"
          role="status"
        >
          <span class="visually-hidden">Loading</span>
        </span>
      </span>
    </div>

    <div
      v-if="isOpen"
      class="fob-op-search-panel shadow-sm border rounded bg-white"
      :style="panelStyle"
    >
      <ul v-if="results.length" class="list-unstyled mb-0" role="listbox">
        <template v-for="(row, idx) in results">
          <li
            v-if="showGroups && isGroupStart(idx)"
            :key="`group-${row._model}`"
            class="fob-op-search-group"
            role="presentation"
          >
            {{ row._modelLabel }}
          </li>
          <li
            :key="String(row._id)"
            role="option"
            :aria-selected="idx === highlighted"
            class="fob-op-search-option"
            :class="{
              'is-highlighted': idx === highlighted,
              'is-current': isCurrent(row),
            }"
            @mouseenter="highlighted = idx"
            @mousedown.prevent="choose(row)"
          >
            <div class="d-flex align-items-center gap-2">
              <span class="fw-semibold text-truncate">{{ rowTitle(row) }}</span>
              <span
                v-if="isCurrent(row)"
                class="badge bg-light text-muted border"
              >
                {{ $t("quickSearch.current") }}
              </span>
            </div>
            <div
              v-if="rowSnippet(row)"
              class="small text-muted fob-op-search-snippet"
            >
              {{ rowSnippet(row) }}
            </div>
          </li>
        </template>
      </ul>

      <div v-else-if="searching" class="p-2 small text-muted">
        {{ $t("quickSearch.searching") }}
      </div>
      <div v-else-if="tooShort" class="p-2 small text-muted">
        {{ $t("quickSearch.hint") }}
      </div>
      <div v-else-if="errored" class="p-2 small text-danger">
        {{ $t("quickSearch.error") }}
      </div>
      <div v-else class="p-2 small text-muted">
        {{ $t("quickSearch.noResults") }}
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;
const PER_PAGE = 20;
/** Con varios modelos se acorta el cupo de cada uno para que entren todos. */
const PER_PAGE_MULTI = 6;
const SNIPPET_PAD = 45;
const DEFAULT_FIELDS = "_id,name,idModel,textPlainVersion,updatedAt";

export default {
  name: "QuickSearch",
  props: {
    /**
     * Modelos a buscar, en orden de aparición: [{ name, label, fields }]. Los
     * nombres los resuelve quien usa el componente contra las definiciones del
     * tenant. `fields` suma campos extra a la respuesta, para que quien navega
     * no tenga que volver a pedir el registro.
     */
    models: { type: Array, default: () => [] },
    /** Atajo para buscar un solo modelo; ignorado si viene `models`. */
    modelName: { type: String, default: "" },
    /** Id del registro abierto, para marcarlo en la lista. */
    currentId: { type: String, default: "" },
    /** Modelo al que pertenece `currentId`, cuando se busca en varios. */
    currentModel: { type: String, default: "" },
    /**
     * Ancla el desplegable con position:fixed. Necesario cuando el buscador vive
     * dentro de un contenedor con overflow (el sidebar recorta un absolute).
     */
    fixedPanel: { type: Boolean, default: false },
    /** Paleta oscura, para usarlo sobre el fondo del menú. */
    dark: { type: Boolean, default: false },
    /** Texto del input; por defecto, el genérico. */
    placeholder: { type: String, default: "" },
  },
  data() {
    return {
      query: "",
      results: [],
      searching: false,
      errored: false,
      isOpen: false,
      highlighted: -1,
      searchTimeout: null,
      requestToken: 0,
      lastTerm: "",
      panelStyle: null,
    };
  },
  computed: {
    term() {
      return this.query.trim();
    },
    tooShort() {
      return this.term.length < MIN_CHARS;
    },
    /** Lista normalizada de modelos a consultar. */
    searchModels() {
      const list = (this.models || [])
        .filter((model) => model && model.name)
        .map((model) => ({
          name: String(model.name),
          label: model.label ? String(model.label) : String(model.name),
          fields: model.fields
            ? `${DEFAULT_FIELDS},${model.fields}`
            : DEFAULT_FIELDS,
        }));
      if (list.length) return list;
      const single = this.modelName || "Proform";
      return [{ name: single, label: single, fields: DEFAULT_FIELDS }];
    },
    showGroups() {
      return this.searchModels.length > 1;
    },
    resolvedPlaceholder() {
      return this.placeholder || this.$t("quickSearch.placeholder");
    },
  },
  watch: {
    isOpen(open) {
      if (!this.fixedPanel) return;
      if (open) {
        this.$nextTick(this.updatePanelPosition);
        this.bindPanelReposition();
      } else {
        this.unbindPanelReposition();
        this.panelStyle = null;
      }
    },
  },
  mounted() {
    document.addEventListener("mousedown", this.onDocumentMouseDown);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.onDocumentMouseDown);
    this.unbindPanelReposition();
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  },
  methods: {
    bindPanelReposition() {
      window.addEventListener("scroll", this.updatePanelPosition, true);
      window.addEventListener("resize", this.updatePanelPosition);
    },
    unbindPanelReposition() {
      window.removeEventListener("scroll", this.updatePanelPosition, true);
      window.removeEventListener("resize", this.updatePanelPosition);
    },
    /** Sigue al input: el panel va con position:fixed y no lo recorta el contenedor. */
    updatePanelPosition() {
      if (!this.fixedPanel || !this.isOpen) return;
      const anchor = this.$el && this.$el.querySelector(".input-group");
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      this.panelStyle = {
        position: "fixed",
        top: `${Math.round(rect.bottom + 4)}px`,
        left: `${Math.round(rect.left)}px`,
        width: `${Math.round(rect.width)}px`,
        right: "auto",
      };
    },
    onDocumentMouseDown(event) {
      if (!this.$el || this.$el.contains(event.target)) return;
      this.close();
    },
    onFocus() {
      this.isOpen = true;
      if (!this.tooShort && this.term !== this.lastTerm) this.scheduleSearch();
    },
    onInput() {
      this.isOpen = true;
      this.errored = false;
      this.highlighted = -1;
      if (this.tooShort) {
        this.results = [];
        this.lastTerm = "";
        this.searching = false;
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
        return;
      }
      this.scheduleSearch();
    },
    scheduleSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searching = true;
      this.searchTimeout = setTimeout(() => this.runSearch(), DEBOUNCE_MS);
    },
    /** Una consulta por modelo; el orden de `searchModels` manda en la lista. */
    async searchOneModel(model, term, perPage) {
      try {
        const params = new URLSearchParams();
        // El backend escapa el término antes de armar el RegExp: mandarlo tal cual.
        params.append("textPlainVersion", term);
        params.append("perPage", String(perPage));
        params.append("page", "1");
        params.append("order", "updatedAt");
        params.append("orderDirection", "desc");
        params.append("fields", model.fields || DEFAULT_FIELDS);
        const { data } = await wencesApi.get(
          `/api/gotrading/dynamic/${encodeURIComponent(
            model.name
          )}?${params.toString()}`
        );
        const rows = Array.isArray(data)
          ? data
          : data && data.items
          ? data.items
          : [];
        return {
          failed: false,
          rows: rows
            .filter((row) => row && row._id)
            .map((row) => ({
              ...row,
              _model: model.name,
              _modelLabel: model.label,
            })),
        };
      } catch (error) {
        console.error("[quickSearch]", model.name, error);
        return { failed: true, rows: [] };
      }
    },
    async runSearch() {
      const term = this.term;
      if (term.length < MIN_CHARS) {
        this.searching = false;
        return;
      }
      const token = ++this.requestToken;
      this.searching = true;
      this.errored = false;

      const models = this.searchModels;
      const perPage = models.length > 1 ? PER_PAGE_MULTI : PER_PAGE;
      const responses = await Promise.all(
        models.map((model) => this.searchOneModel(model, term, perPage))
      );
      if (token !== this.requestToken) return;

      this.results = responses.reduce((all, res) => all.concat(res.rows), []);
      this.lastTerm = term;
      this.highlighted = this.results.length ? 0 : -1;
      // Solo es un error si ningún modelo respondió: si uno falla, se muestra el resto.
      this.errored =
        !this.results.length && responses.every((res) => res.failed);
      this.searching = false;
    },
    isGroupStart(idx) {
      if (idx === 0) return true;
      const prev = this.results[idx - 1];
      const row = this.results[idx];
      return !prev || !row || prev._model !== row._model;
    },
    isCurrent(row) {
      if (!this.currentId || String(row._id) !== String(this.currentId)) {
        return false;
      }
      // Con varios modelos el id solo cuenta dentro del modelo de la fila abierta.
      return !this.showGroups || row._model === this.currentModel;
    },
    rowTitle(row) {
      const id =
        row.idModel != null && row.idModel !== "" ? `#${row.idModel}` : "";
      const name = row.name ? String(row.name) : "";
      if (id && name) return `${id} — ${name}`;
      return name || id || String(row._id);
    },
    /** Fragmento del texto plano alrededor de la coincidencia. */
    rowSnippet(row) {
      const text = row.textPlainVersion ? String(row.textPlainVersion) : "";
      if (!text) return "";
      const flat = text.replace(/\s+/g, " ").trim();
      if (!flat) return "";
      const at = flat.toLowerCase().indexOf(this.term.toLowerCase());
      if (at < 0) return flat.slice(0, SNIPPET_PAD * 2);
      const from = Math.max(0, at - SNIPPET_PAD);
      const to = Math.min(flat.length, at + this.term.length + SNIPPET_PAD);
      return `${from > 0 ? "…" : ""}${flat.slice(from, to)}${
        to < flat.length ? "…" : ""
      }`;
    },
    moveHighlight(delta) {
      if (!this.results.length) return;
      this.isOpen = true;
      const next = this.highlighted + delta;
      if (next < 0) this.highlighted = this.results.length - 1;
      else if (next >= this.results.length) this.highlighted = 0;
      else this.highlighted = next;
    },
    chooseHighlighted() {
      const row = this.results[this.highlighted];
      if (row) this.choose(row);
    },
    choose(row) {
      if (!row || !row._id) return;
      const id = String(row._id);
      this.close();
      this.query = "";
      this.results = [];
      this.lastTerm = "";
      if (this.isCurrent(row)) return;
      this.$emit("select", id, row._model || this.searchModels[0].name, row);
    },
    clear() {
      this.query = "";
      this.results = [];
      this.lastTerm = "";
      this.errored = false;
      this.highlighted = -1;
      this.searching = false;
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      if (this.$refs.input) this.$refs.input.focus();
    },
    close() {
      this.isOpen = false;
      this.highlighted = -1;
    },
  },
};
</script>

<style scoped>
.fob-op-search {
  position: relative;
  width: 100%;
  max-width: 360px;
}

.fob-op-search.is-fixed-panel {
  max-width: none;
}

.fob-op-search-panel {
  position: absolute;
  z-index: 1050;
  top: calc(100% + 4px);
  right: 0;
  left: 0;
  max-height: 340px;
  overflow-y: auto;
}

.fob-op-search-option {
  padding: 0.35rem 0.6rem;
  cursor: pointer;
  border-bottom: 1px solid #f1f3f5;
}

.fob-op-search-option:last-child {
  border-bottom: 0;
}

.fob-op-search-option.is-highlighted {
  background-color: #eef4ff;
}

.fob-op-search-option.is-current {
  cursor: default;
  opacity: 0.7;
}

.fob-op-search-snippet {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.fob-op-search-group {
  padding: 0.3rem 0.6rem 0.15rem;
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #868e96;
  background-color: #f8f9fa;
  border-bottom: 1px solid #f1f3f5;
}

/* Variante oscura: el buscador vive sobre el fondo del menú. */
.fob-op-search.is-dark .form-control,
.fob-op-search.is-dark .input-group-text {
  background-color: rgba(0, 0, 0, 0.35) !important;
  border-color: rgba(255, 255, 255, 0.15);
  color: #fff;
}

.fob-op-search.is-dark .form-control::placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.fob-op-search.is-dark .form-control:focus {
  background-color: rgba(0, 0, 0, 0.45) !important;
  border-color: rgba(255, 255, 255, 0.35);
  box-shadow: none;
  color: #fff;
}

.fob-op-search.is-dark .input-group-text .fa-search {
  color: rgba(255, 255, 255, 0.65) !important;
}

.fob-op-search.is-dark .btn-outline-secondary {
  border-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.7);
}

.fob-op-search.is-dark .btn-outline-secondary:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.fob-op-search.is-dark .fob-op-search-panel {
  background-color: #1f2429 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #e9ecef;
}

.fob-op-search.is-dark .fob-op-search-group {
  color: rgba(255, 255, 255, 0.5);
  background-color: rgba(255, 255, 255, 0.04);
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.fob-op-search.is-dark .fob-op-search-option {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.fob-op-search.is-dark .fob-op-search-option.is-highlighted {
  background-color: rgba(255, 255, 255, 0.1);
}

.fob-op-search.is-dark .text-muted {
  color: rgba(255, 255, 255, 0.55) !important;
}

.fob-op-search.is-dark .badge.bg-light {
  background-color: rgba(255, 255, 255, 0.12) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
  color: rgba(255, 255, 255, 0.75) !important;
}
</style>
